'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const Relay = require('react-relay');
const get = require('lodash.get');
const classNames = require('classnames');
const tokenReplace = require('dibs-token-replace');
const User = require('../../../global/models/User');
const ContextComponent = require('../../../global/components/ContextComponent');
const enabler = require('../../../global/utils/enabler.es');
const ContactDealerMessage = require('./ContactDealerMessage');
const ContactDealerMessageSent = require('./ContactDealerMessageSent');
const ContactDealerFavorite = require('./ContactDealerFavorite');
const CallDealerButton = require('./CallDealerButton');
const RequestMoreInfoLinks = require('../../../contact/dealer/components/RequestMoreInfoLinks');
const {
    trackContactDealer,
    trackDealerLinkClick,
    trackExpandDealerInfo,
    trackDealerInfoClick,
    trackMakeOfferClick,
    trackAddToCart,
    trackCheckoutRedirect
} = require('../../actions/pdpTrackingActions');
require('dibs-buyer-initiate-call/dist/styles.css');

const contactDealerActions = require('../../actions/contactDealerActions.es');
const transactionActions = require('../../../contact/shared/actions/transactionActions.es');
const pdpTracking = require('../../helpers/pdpTracking');
const copyText = require('copy-text')().extend(
    require('../../../../../json/copy/contact-dealer.json')
);
const { variants } = require('../../constants/maytestConstants.es');
const { shouldShowHoldRequest } = require('../../helpers/holdRequest.es');
const HoldRequestMutation = require('./HoldRequestMutation').default;


// Helper functions
function makeLocation({city, state, country}) {
    return `${city}, ${state || country}`;
}

function locationCopy({copy, city, state, country}) {
    return tokenReplace(get(copy, 'dealer.location'), {location: makeLocation({city, state, country})});
}

function formatCityStateZip({city, state, zip}) {
    return [[city, 'addressLocality'], [state, 'addressRegion'], [zip, 'postalCode']].reduce((acc, item) => {
        const [value = '', key = ''] = item;

        if (typeof value === 'string') {
            acc.push(`<span data-tn="dealer-more-info-${key}">${value.trim()}</span>`);
        }

        return acc;
    }, []).join('<span>,&nbsp;</span>');
}

/**
 * @param {string} dealerReferenceTitle - Title-text for dealer reference number.
 * @param {string} dealerReferenceNumber - Item dealer reference number.
 */
function dealerReferenceCopy({dealerReferenceTitle, dealerReferenceNumber}) {
    if (dealerReferenceNumber && dealerReferenceTitle) {
        return (
            <span>{dealerReferenceTitle}: {dealerReferenceNumber}</span>
        );
    }
    return null;
}

dealerReferenceCopy.propTypes = {
    dealerReferenceTitle: PropTypes.string,
    dealerReferenceNumber: PropTypes.string
};

const DEALER_TYPE_GALLERY = 'gallery';
const DEALER_TYPE_DEALER = 'dealer';
const DEALER_TYPE_SELLER = 'seller';

const MORE_INFO_OFFSET_TOP = 5;


class ContactDealerBody extends ContextComponent {
    constructor(props) {
        super(props);

        const {city, state, country, zip, type, sinceYear, dealerReferenceNumber, dealerReferenceTitle} = props.dealerData;
        const copy = props.copy;
        this.state = {
            nameError: false,
            messageError: false,
            moreInfoExpanded: false,
            offerExpanded: false,
            callDealerIsOpened: false,
            isHoldRequestChecked: false
        };
        this._moreInfoHeight = null;
        this._cityStateZip = formatCityStateZip({copy, city, state, zip});
        this._locationCopy = locationCopy({copy, city, state, country});
        this._sinceYearCopy = tokenReplace(get(copy, 'dealer.sinceYear'), {type, sinceYear});
        this._dealerReferenceCopy = dealerReferenceCopy({dealerReferenceTitle, dealerReferenceNumber});
        this.onContactDealer = this.onContactDealer.bind(this);
        this.onMoreInfoClick = this.onMoreInfoClick.bind(this);
        this.onDealerClick = this.onDealerClick.bind(this);
        this.onMessageFocused = this.onMessageFocused.bind(this);
        this.onMessageEntered = this.onMessageEntered.bind(this);
        this.onNameEntered = this.onNameEntered.bind(this);
        this.onOfferClick = this.onOfferClick.bind(this);
        this.onOfferEntered = this.onOfferEntered.bind(this);
        this.onOfferBlurred = this.onOfferBlurred.bind(this);
        this.onHoldRequestClick = this.onHoldRequestClick.bind(this);

        this.sentFrom = null;
    }
    componentDidUpdate(prevProps) {
        const { isDealerMessageSent, sidebar, isPrintDealer } = this.props;
        const sentFromSameBody = (sidebar && this.sentFrom === 'sidebar') || (!sidebar && this.sentFrom === 'body');

        if (isDealerMessageSent && !prevProps.isDealerMessageSent && !isPrintDealer && sentFromSameBody) {
            let trackingObj = {
                action: 'submitted'
            };
            if (sidebar) {
                trackingObj = { ...trackingObj, suffix: 'sidebar' };
            }
            this.context.executeAction(trackContactDealer, trackingObj);
        }
    }
    setWhereWasSentFrom(sidebar) {
        this.sentFrom = sidebar ? 'sidebar' : 'body';
    }
    onContactDealer() {
        const { dealerData: {company}, errors, backboneItem: item, offer, offerAmount, price,
                userDisplayName, userMessage, sidebar, userId, itemId, vertical, relay } = this.props;
        const hasDisplay = User.loggedIn() || userDisplayName;
        let trackingObj = {
            action: 'send clicked'
        };
        if (sidebar) {
            trackingObj = { ...trackingObj, suffix: 'sidebar' };
        }
        if (!hasDisplay || !userMessage) {
            this.setState({
                messageError: !userMessage,
                nameError: !userDisplayName
            });
        } else if (offerAmount && !errors.length) {
            this.context.executeAction(trackCheckoutRedirect, {offerAmount, price});
            this.context.executeAction(contactDealerActions.submitOffer, {item, offer});
        } else if (User.loggedIn()) {
            this.setWhereWasSentFrom(sidebar);
            if (this.state.isHoldRequestChecked) {
                relay.commitUpdate(new HoldRequestMutation({
                    userId,
                    itemId,
                    sellerId: this.props.item.seller.serviceId,
                    vertical
                }), {
                    onSuccess: () => {
                        this.context.executeAction(contactDealerActions.submitMessageLoggedIn, User.getCurrentUser().toJSON());
                    }
                });
            } else {
                this.context.executeAction(contactDealerActions.submitMessageLoggedIn, User.getCurrentUser().toJSON());
            }
            this.context.executeAction(trackContactDealer, trackingObj);
        } else {
            this.setWhereWasSentFrom(sidebar);
            this.context.executeAction(contactDealerActions.submitMessageNotLoggedIn, {company});
            this.context.executeAction(trackContactDealer, trackingObj);
        }
    }
    onDealerClick() {
        this.context.executeAction(trackDealerLinkClick, { location: 'dealer logo' });
    }
    onMessageEntered(e) {
        const { value } = e.target;
        this.setState({messageError: !value});
        this.context.executeAction(contactDealerActions.enterMessage, {value});
    }
    onMessageFocused() {
        const { hasTrackingFired, sidebar } = this.props;
        if (!hasTrackingFired) {
            let trackingObj = {
                action: 'clicked'
            };
            if (sidebar) {
                trackingObj = { ...trackingObj, suffix: 'sidebar' };
            }
            this.context.executeAction(trackContactDealer, trackingObj);
        }
    }
    onMoreInfoClick() {
        const {itemPk} = this.props;
        const height = get(this, 'refs.moreInfo.offsetHeight');
        const moreInfoExpanded = !this.state.moreInfoExpanded;

        if (!this._moreInfoHeight && height) {
            this._moreInfoHeight = height;
        }

        if (moreInfoExpanded) {
            this.context.executeAction(trackDealerInfoClick, {url: this.props.canonicalUrl});
            this.context.executeAction(trackExpandDealerInfo, {itemPk});
        }
        this.setState({moreInfoExpanded});
    }
    onNameEntered(e) {
        const { value } = e.target;
        this.setState({nameError: !value});
        this.context.executeAction(contactDealerActions.enterName, {value});
    }
    onOfferClick() {
        const { isNegotiable, isPreQuote, isReturnable, backboneItem: item, backboneUser: {user} } = this.props;
        const { offerExpanded } = this.state;
        if (!offerExpanded) {
            this.context.executeAction(trackMakeOfferClick, {isNegotiable, isPreQuote, isReturnable});
            this.context.executeAction(trackAddToCart, {trackingVpvParams: pdpTracking.getInitialDataLayerInfo()});
        }
        this.setState({offerExpanded: !offerExpanded});
        this.context.executeAction(contactDealerActions.initializeOffer, {item, user});
    }
    onOfferEntered(e) {
        const { value: rawValue } = e.target;
        const field = 'amountPerItem.amount';
        const value = rawValue.replace(/[^\d]+/g, '');
        this.context.executeAction(transactionActions.editOfferField, {field, value});
    }
    onOfferBlurred() {
        const { backboneUser: user } = this.props;
        this.context.executeAction(transactionActions.validateOffer, {
            copyText,
            bypassLowball: true,
            showLowballOptions: false,
            user
        });
    }
    _getMoreInfoWrapperStyle() {
        const {moreInfoExpanded} = this.state;
        const moreInfoHeight = this._moreInfoHeight || 0;
        return {
            maxHeight: moreInfoExpanded ? `${moreInfoHeight + MORE_INFO_OFFSET_TOP}px` : '0px',
            paddingTop: moreInfoExpanded ? `${MORE_INFO_OFFSET_TOP}px` : '0px'
        };
    }
    _makeAssociationList(associations = []) {
        if (Array.isArray(associations)) {

            const elements = associations.reduce((memo, association, index)=> {
                association = get(association, 'displayName');
                memo.push(<li key={index}>{association}</li>);
                return memo;
            }, []);
            const { maytest } = this.props;

            const associationsContainer = (
                <div className="pdp-dealer-body-associations">
                    <span className="pdp-dealer-body-title">Associations{maytest === variants.OPOP ? ':' : ''}</span>
                    <ul>{elements}</ul>
                </div>
            );

            return associations.length > 0 ?
                maytest === variants.OPOP ? (
                    <div className="pdp-dealer-company-info">
                        {associationsContainer}
                    </div>
                ) : (
                    <div>
                        <div className="pdp-dealer-body-divider" />
                        {associationsContainer}
                    </div>
                ) : null;
        } else {
            return null;
        }
    }
    onHoldRequestClick(checked) {
        this.setState({isHoldRequestChecked: checked});
    }
    render() {
        const {
            contactDealerCTATestRunning,
            copy,
            currency,
            errors,
            favoriteDealer,
            favoriteDealerCta,
            handleContactDealerCtaClick,
            isDealerMessageSending,
            isDealerMessageSent,
            isNegotiable,
            isPrintDealer,
            item,
            itemId,
            offerAmount,
            optimizelyVariant,
            price,
            relay,
            user,
            userId,
            viewer,
            dealerData: {
                address,
                address2,
                company,
                country,
                logo,
                pk,
                telephone,
                type,
                uri,
                preferences: dealerPreferences,
                association,
                city,
                state,
                sinceYear
            },
            maytest,
            sellerResponseTime,
            sidebar,
            onContactDealerModal
        } = this.props;
        const {hasUserId} = relay.variables;
        const { moreInfoExpanded: expanded, offerExpanded, nameError, messageError, isHoldRequestChecked } = this.state;
        const favoriteProps = {company, favoriteDealer, favoriteDealerCta, pk, uri, type, maytest};

        const detailDataTags = isPrintDealer ? 'print-item-details-dealer' : 'item-details-dealer';
        const moreInfoCopy = get(copy, `dealer.${expanded ? 'infoExpanded' : 'infoCollapsed'}`);
        const [error] = errors.map(e => e.message);
        const isVariant = optimizelyVariant === 'submitOffer';
        const { displayStoreAddress, storeAddressByAppointmentOnly } = dealerPreferences || {};

        const isCallButtonEnabled = enabler.isEnabled('call_dealer_button');
        const canMakeCall = get(item, 'seller.canMakeCall', false);
        const showCallDealerButton = (isCallButtonEnabled && telephone) || canMakeCall;
        const showPhone = !isCallButtonEnabled && canMakeCall === false;

        const showAddress = displayStoreAddress === 'Y';
        const showByAppointmentOnly = storeAddressByAppointmentOnly === 'Y';
        const bodyClasses = classNames('pdp-dealer-body', {
            'in-sidebar': sidebar,
            'center-dealer-info': maytest === variants.CONTACT2,
            'center-in-column': maytest === variants.CONTACT3 && sidebar,
            'has-maytest-opop': maytest === variants.OPOP
        });
        const dealerInfoClassses = classNames('pdp-dealer-info', 'pdp-dealer-section', {
            'pdp-dealer-maytest-opop': maytest === variants.OPOP,
            'center-dealer-info': maytest === variants.CONTACT2
        });
        const messageArea = isDealerMessageSent ? (
            <ContactDealerMessageSent
                {...favoriteProps}
            />
        ) : (
            <ContactDealerMessage
                currency={currency}
                error={error}
                isDealerMessageSending={isDealerMessageSending}
                isLoggedIn={User.loggedIn()}
                displayOffer={isVariant && isNegotiable && !!price}
                messageError={messageError}
                nameError={nameError}
                offerAmount={offerAmount}
                offerExpanded={offerExpanded}
                onContactDealer={this.onContactDealer}
                onMessageEntered={this.onMessageEntered}
                onMessageFocused={this.onMessageFocused}
                onNameEntered={this.onNameEntered}
                onOfferClick={this.onOfferClick}
                onOfferEntered={this.onOfferEntered}
                onOfferBlurred={this.onOfferBlurred}
                price={price}
                type={type}
                maytest={maytest}
                sidebar={sidebar}
                showRequestHold={shouldShowHoldRequest(item, user)}
                isHoldRequestChecked={isHoldRequestChecked}
                onHoldRequestClick={this.onHoldRequestClick}
            />
        );
        return !sidebar || maytest !== variants.CONTACT3 ? (
            <div className={bodyClasses} data-tn={detailDataTags}>
                {(!sidebar && maytest === variants.OPOP) &&
                    <div className={dealerInfoClassses} data-tn="pdp-dealer-info">
                        <div className="pdp-contact-dealer-title header-primary-large pdp-secondary-header">Contact The Seller</div>
                        {sellerResponseTime &&
                            <p className="pdp-dealer-company-info">
                                <span className="pdp-dealer-body-title">Response Time:</span> Usually within {sellerResponseTime}
                            </p>
                        }
                        <p className="pdp-dealer-company-info">
                            <span className="pdp-dealer-body-title">1stdibs seller since</span> {sinceYear}
                        </p>
                        <p className="pdp-dealer-company-info">
                            <span className="pdp-dealer-body-title">Located in</span> {makeLocation({city, state, country})}
                        </p>
                        { this._makeAssociationList(association ? association.slice(0, 3) : null) }
                        {showCallDealerButton &&
                            <CallDealerButton userId={userId} hasUserId={hasUserId} item={item} viewer={viewer} copy={copy} dealerPk={pk} page={uri} />
                        }
                    </div>
                }
                {(!sidebar && maytest !== variants.OPOP) &&
                    <div className={dealerInfoClassses} data-tn="pdp-dealer-info">
                        {maytest === variants.CONTACT2 && <div className="pdp-contact-dealer-title header-primary-large pdp-secondary-header">About The Seller</div>}
                        <div><a href={uri} onClick={this.onDealerClick}><img className="pdp-dealer-logo" src={logo} alt={company} /></a></div>

                        {showCallDealerButton &&
                            <CallDealerButton userId={userId} hasUserId={hasUserId} item={item} viewer={viewer} copy={copy} dealerPk={pk} page={uri} />
                        }

                        <div className="pdp-dealer-info-wrapper" onClick={this.onMoreInfoClick}>
                            <div className="pdp-dealer-link" dangerouslySetInnerHTML={{__html: moreInfoCopy}}/>
                            <div className="pdp-dealer-more-info-wrapper" style={this._getMoreInfoWrapperStyle()}>
                                <div className="pdp-dealer-more-info" ref="moreInfo">
                                    {showAddress && <span>{copyText.get('address')}</span>}
                                    <div data-tn="dealer-more-info-company">{company}</div>
                                    {showAddress &&
                                        <div>
                                            <div data-tn="dealer-more-info-address">{address}</div>
                                            <div data-tn="dealer-more-info-address2">{address2}</div>
                                            <div dangerouslySetInnerHTML={{__html: this._cityStateZip}}/>
                                            <div data-tn="dealer-more-info-country">{country}</div>
                                        </div>
                                    }
                                    {showByAppointmentOnly &&
                                        <div className="pdp-dealer-more-info-by-appointment">
                                            {copyText.get('storeAddressByAppointmentOnly')}
                                        </div>
                                    }

                                    {showPhone && <div data-tn="dealer-more-info-telephone">{telephone}</div>}

                                    <div data-tn="dealer-more-info-reference">{this._dealerReferenceCopy}</div>
                                    { this._makeAssociationList(association) }
                                </div>
                            </div>
                        </div>

                        <p className="pdp-dealer-company-info">{this._sinceYearCopy}</p>
                        <p className="pdp-dealer-company-info">{this._locationCopy}</p>
                        {!isDealerMessageSent && <ContactDealerFavorite {...favoriteProps} />}
                    </div>
                }
                {(sidebar || maytest !== variants.CONTACT2) && messageArea}
                {!sidebar && contactDealerCTATestRunning && user && user.isVerifiedTrade && (
                    <div className="pdp-dealer-request-more-info">
                        <RequestMoreInfoLinks
                            hasUserId={hasUserId}
                            userId={userId}
                            itemId={itemId}
                            viewer={viewer}
                            user={user}
                            item={item}
                            handleCtaClick={handleContactDealerCtaClick}
                        />
                    </div>
                )}
            </div>
        ) : (
            <div className={bodyClasses} data-tn={detailDataTags}>
                <div className="pdp-contact-dealer-title">Questions about this piece?</div>
                <button
                    className="button-large button-secondary"
                    onClick={onContactDealerModal}
                >
                    {`Ask the ${type}`}
                </button>
            </div>
        );
    }
}

ContactDealerBody.propTypes = {
    contactDealerCTATestRunning: PropTypes.bool.isRequired,
    canonicalUrl: PropTypes.string.isRequired,
    copy: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    errors: PropTypes.array,
    favoriteDealer: PropTypes.object.isRequired,
    favoriteDealerCta: PropTypes.object,
    itemId: PropTypes.string.isRequired,
    itemPk: PropTypes.string.isRequired,
    optimizelyVariant: PropTypes.string,
    offerAmount: PropTypes.string,
    price: PropTypes.number.isRequired,
    vertical: PropTypes.string.isRequired,
    handleContactDealerCtaClick: PropTypes.func.isRequired,
    dealerData: PropTypes.shape({
        uri: PropTypes.string.isRequired,
        logo: PropTypes.string.isRequired,
        company: PropTypes.string.isRequired,
        sinceYear: PropTypes.string.isRequired,
        address: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string.isRequired,
        state: PropTypes.string,
        country: PropTypes.string,
        zip: PropTypes.string,
        telephone: PropTypes.string,
        pk: PropTypes.string.isRequired,
        urlLabel: PropTypes.string.isRequired,
        type: PropTypes.oneOf([DEALER_TYPE_GALLERY, DEALER_TYPE_DEALER, DEALER_TYPE_SELLER]).isRequired,
        association: PropTypes.array,
        preferences: PropTypes.object.isRequired
    }).isRequired,
    maytest: PropTypes.string,
    sellerResponseTime: PropTypes.string,
    sidebar: PropTypes.bool,
    onContactDealerModal: PropTypes.func
};


module.exports = Relay.createContainer(ContactDealerBody, {
    initialVariables: {
        hasUserId: false,
        userId: null,
        itemId: null
    },
    fragments: {
        viewer: ({hasUserId, userId, itemId}) => Relay.QL`
            fragment on Viewer {
                ${CallDealerButton.getFragment('viewer', {hasUserId, userId})}
                ${RequestMoreInfoLinks.getFragment('viewer', {hasUserId, userId, itemId})}
            }
        `,
        user: ({hasUserId, userId, itemId}) => Relay.QL`
            fragment on User {
                ${RequestMoreInfoLinks.getFragment('user', {hasUserId, userId, itemId})}
                isVerifiedTrade
                canUseNetPrice: hasPermission(name: "B_NET_PRICE")
            }
        `,
        item: ({hasUserId, userId, itemId}) => Relay.QL`
            fragment on Item {
                ${CallDealerButton.getFragment('item', {hasUserId, userId})}
                ${RequestMoreInfoLinks.getFragment('item', {hasUserId, userId, itemId})}
                isFurniture
                hasRequestedHoldForBuyer(buyerId: $userId) @include(if: $hasUserId)
                isPriceUponRequest
                isOnHold
                isSold
                attributes {
                    customizable {
                        value
                    }
                }
                seller {
                    serviceId
                    canMakeCall: isWhitelisted(listName: "ALLOW_TRACKED_CALLS")
                    sellerProfile {
                        company
                    }
                }
            }
        `
    }
});



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/ContactDealer/ContactDealerBody.jsx